import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Button,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  useToast,
  Badge,
  Spinner,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Card,
  CardBody,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useDisclosure,
  Divider,
} from '@chakra-ui/react';
import { FiSearch, FiRefreshCw, FiEye, FiCalendar, FiDollarSign, FiDownload, FiShoppingBag, FiUsers, FiPackage, FiMapPin, FiAlertTriangle } from 'react-icons/fi';
import DataTable from 'react-data-table-component';
import { productRetailTransactionApi } from '../config/api';

const getDefaultDateRange = () => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - 30);

  const toInputDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    dateFrom: toInputDate(startDate),
    dateTo: toInputDate(endDate),
  };
};

const ReportingTab = ({ allData = false }) => {
  const toast = useToast();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const [searchText, setSearchText] = useState('');
  const { isOpen: isDetailOpen, onOpen: onDetailOpen, onClose: onDetailClose } = useDisclosure();
  const defaultDateRange = getDefaultDateRange();
  
  const [filters, setFilters] = useState({
    dateFrom: defaultDateRange.dateFrom,
    dateTo: defaultDateRange.dateTo,
    orderRef: '',
    customerName: '',
    receiptNumber: '',
    location: '',
    returnStatus: '',
    isReturnOrder: '',
    isExchangeOrder: '',
    onlyInjourneyBesties: allData ? false : true, // allData: show all; IEEM: default "Injourney Besties"
    limit: 10,
    offset: 0
  });

  // Fetch transactions
  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const params = { ...filters };
      
      // Remove empty values, but keep onlyInjourneyBesties
      Object.keys(params).forEach(key => {
        if (key === 'onlyInjourneyBesties') {
          return;
        }
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });
      if (allData) params.allData = true;

      const response = await productRetailTransactionApi.get('/list', { params });

      if (response.data && response.data.status === 200) {
        setTransactions(response.data.data || []);
        setTotalRows(response.data.total || 0);
      } else {
        toast({
          title: 'Error',
          description: response.data?.message || 'Failed to fetch transactions',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to fetch transactions',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [filters, allData, toast]);

  // Fetch summary - use same filters as transactions
  const fetchSummary = useCallback(async () => {
    try {
      const params = {};
      // Apply all filters that affect summary
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.location) params.location = filters.location;
      if (filters.orderRef) params.orderRef = filters.orderRef;
      if (filters.customerName) params.customerName = filters.customerName;
      if (filters.receiptNumber) params.receiptNumber = filters.receiptNumber;
      if (filters.returnStatus) params.returnStatus = filters.returnStatus;
      if (filters.isReturnOrder !== undefined && filters.isReturnOrder !== '') params.isReturnOrder = filters.isReturnOrder;
      if (filters.isExchangeOrder !== undefined && filters.isExchangeOrder !== '') params.isExchangeOrder = filters.isExchangeOrder;
      params.onlyInjourneyBesties = allData ? false : (filters.onlyInjourneyBesties !== false);
      if (allData) params.allData = true;

      const response = await productRetailTransactionApi.get('/summary/stats', { params });

      if (response.data && response.data.status === 200) {
        setSummary(response.data.data || {});
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  }, [filters, allData]);

  useEffect(() => {
    fetchTransactions();
    fetchSummary();
  }, [fetchTransactions, fetchSummary]);

  // Fetch transaction detail
  const fetchTransactionDetail = async (id) => {
    try {
      const response = await productRetailTransactionApi.get(`/${id}`);
      if (response.data && response.data.status === 200) {
        setSelectedTransaction(response.data.data);
        onDetailOpen();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to fetch transaction detail',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle filter change
  const handleFilterChange = (name, value) => {
    setFilters(prev => {
      const next = { ...prev, [name]: value };
      if (name !== 'offset') next.offset = 0; // Reset to page 1 when other filters change (not when changing page)
      return next;
    });
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      dateFrom: defaultDateRange.dateFrom,
      dateTo: defaultDateRange.dateTo,
      orderRef: '',
      customerName: '',
      receiptNumber: '',
      location: '',
      returnStatus: '',
      isReturnOrder: '',
      isExchangeOrder: '',
      onlyInjourneyBesties: allData ? false : true,
      limit: 10,
      offset: 0
    });
  };

  // Handle sync
  const handleSync = async () => {
    if (!filters.dateFrom || !filters.dateTo) {
      toast({
        title: 'Validation Error',
        description: 'Please select both start date and end date',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setSyncing(true);
      const response = await productRetailTransactionApi.post('/sync', {
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo
      }, {
        timeout: 120000 // 2 minutes for sync operation
      });

      if (response.data && response.data.status === 200) {
        const result = response.data.data;
        toast({
          title: 'Sync Successful',
          description: `Synced ${result.successCount} transactions. ${result.errorCount > 0 ? `${result.errorCount} errors occurred.` : ''}`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        
        // Refresh transactions and summary after sync
        fetchTransactions();
        fetchSummary();
      } else {
        toast({
          title: 'Error',
          description: response.data?.message || 'Failed to sync data',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error syncing data:', error);
      let errorMessage = 'Failed to sync data';
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Request timeout. The sync operation is taking longer than expected. Please try again or reduce the date range.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSyncing(false);
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    if (!value) return 'Rp 0';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter transactions based on search text (client-side)
  const filteredTransactions = React.useMemo(() => {
    if (!searchText) return transactions;
    const searchLower = searchText.toLowerCase();
    return transactions.filter(row => {
      // Get customer name or referral_code2 as fallback
      const customerName = row.customer_name && row.customer_name.trim() !== '' && row.customer_name !== '-' 
        ? row.customer_name 
        : (row.referral_code2 || '');
      
      return (
        (row.order_ref && row.order_ref.toLowerCase().includes(searchLower)) ||
        (customerName && customerName.toLowerCase().includes(searchLower)) ||
        (row.receipt_number && row.receipt_number.toLowerCase().includes(searchLower)) ||
        (row.location && row.location.toLowerCase().includes(searchLower))
      );
    });
  }, [transactions, searchText]);

  const exceptionInsights = React.useMemo(() => {
    const pendingReturn = transactions.filter((row) => String(row.return_status || '').toLowerCase() === 'pending').length;
    const returnOrders = transactions.filter((row) => Number(row.is_return_order) === 1).length;
    const exchangeOrders = transactions.filter((row) => Number(row.is_exchange_order) === 1).length;
    const missingCustomer = transactions.filter((row) => {
      const customerName = row.customer_name;
      return !customerName || customerName.trim() === '' || customerName === '-';
    }).length;
    return { pendingReturn, returnOrders, exchangeOrders, missingCustomer };
  }, [transactions]);

  const applyExceptionDrilldown = (key) => {
    if (key === 'pending-return') {
      handleFilterChange('returnStatus', 'pending');
    } else if (key === 'return-order') {
      handleFilterChange('isReturnOrder', 1);
      handleFilterChange('isExchangeOrder', '');
    } else if (key === 'exchange-order') {
      handleFilterChange('isExchangeOrder', 1);
      handleFilterChange('isReturnOrder', '');
    } else if (key === 'missing-customer') {
      setSearchText('-');
    }
  };

  // DataTable columns
  const columns = [
    {
      name: 'Order Date',
      selector: row => formatDate(row.date_order),
      sortable: true,
      minWidth: '160px',
      wrap: true
    },
    {
      name: 'Order Ref',
      selector: row => row.order_ref || '-',
      sortable: true,
      minWidth: '140px',
      wrap: true
    },
    {
      name: 'Customer',
      selector: row => {
        const customerName = row.customer_name;
        // If customer_name is null, empty, or '-', use referral_code2
        if (!customerName || customerName.trim() === '' || customerName === '-') {
          return row.referral_code2 || '-';
        }
        return customerName;
      },
      sortable: true,
      minWidth: '150px',
      wrap: true
    },
    {
      name: 'Receipt Number',
      selector: row => row.receipt_number || '-',
      sortable: true,
      minWidth: '140px',
      wrap: true
    },
    {
      name: 'Location',
      selector: row => row.location || '-',
      sortable: true,
      minWidth: '120px',
      wrap: true
    },
    {
      name: 'Total',
      selector: row => row.total || 0,
      sortable: true,
      cell: row => (
        <Text fontWeight="semibold" fontSize="sm">{formatCurrency(row.total)}</Text>
      ),
      minWidth: '130px',
      right: true
    },
    {
      name: 'Status',
      cell: row => (
        <HStack spacing={1} flexWrap="wrap">
          {row.is_return_order === 1 && (
            <Badge colorScheme="red" fontSize="xs">Return</Badge>
          )}
          {row.is_exchange_order === 1 && (
            <Badge colorScheme="orange" fontSize="xs">Exchange</Badge>
          )}
          {row.return_status && (
            <Badge colorScheme="purple" fontSize="xs">{row.return_status}</Badge>
          )}
          {!row.is_return_order && !row.is_exchange_order && !row.return_status && (
            <Badge colorScheme="green" fontSize="xs">Normal</Badge>
          )}
        </HStack>
      ),
      minWidth: '120px',
      wrap: true
    },
    {
      name: 'Actions',
      cell: row => (
        <Button
          size="xs"
          leftIcon={<FiEye />}
          onClick={() => fetchTransactionDetail(row.id)}
          colorScheme="blue"
        >
          View
        </Button>
      ),
      minWidth: '80px',
      right: true
    }
  ];

  return (
    <VStack align="stretch" spacing={4}>
      {/* Date + Sync Section */}
      <Box bg="white" p={4} borderRadius="md" boxShadow="sm" borderLeft="4px" borderColor="blue.500">
        <VStack align="stretch" spacing={4}>
          <HStack>
            <Text fontWeight="bold" fontSize="lg">Date Range & Sync Control</Text>
          </HStack>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <FiCalendar color="gray.300" />
              </InputLeftElement>
              <Input
                type="date"
                placeholder="Start Date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              />
            </InputGroup>
            
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <FiCalendar color="gray.300" />
              </InputLeftElement>
              <Input
                type="date"
                placeholder="End Date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              />
            </InputGroup>
            
            <Button
              leftIcon={<FiDownload />}
              colorScheme="blue"
              onClick={handleSync}
              isLoading={syncing}
              loadingText="Syncing..."
              isDisabled={!filters.dateFrom || !filters.dateTo}
            >
              Sync Data
            </Button>
          </SimpleGrid>
        </VStack>
      </Box>

      {/* Filters */}
      <Box bg="white" p={4} borderRadius="md" boxShadow="sm">
        <Text fontWeight="semibold" color="gray.700" mb={3}>
          Advanced Filters
        </Text>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
          <Input
            placeholder="Order Ref"
            value={filters.orderRef}
            onChange={(e) => handleFilterChange('orderRef', e.target.value)}
          />
          
          <Input
            placeholder="Customer Name"
            value={filters.customerName}
            onChange={(e) => handleFilterChange('customerName', e.target.value)}
          />
          
          <Input
            placeholder="Receipt Number"
            value={filters.receiptNumber}
            onChange={(e) => handleFilterChange('receiptNumber', e.target.value)}
          />
          
          <Input
            placeholder="Location"
            value={filters.location}
            onChange={(e) => handleFilterChange('location', e.target.value)}
          />
          
          <Select
            placeholder="Return Status"
            value={filters.returnStatus}
            onChange={(e) => handleFilterChange('returnStatus', e.target.value)}
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </Select>
          
          <Select
            placeholder="Order Type"
            value={filters.isReturnOrder !== '' ? filters.isReturnOrder : filters.isExchangeOrder !== '' ? 'exchange' : ''}
            onChange={(e) => {
              if (e.target.value === 'return') {
                handleFilterChange('isReturnOrder', 1);
                handleFilterChange('isExchangeOrder', '');
              } else if (e.target.value === 'exchange') {
                handleFilterChange('isExchangeOrder', 1);
                handleFilterChange('isReturnOrder', '');
              } else {
                handleFilterChange('isReturnOrder', '');
                handleFilterChange('isExchangeOrder', '');
              }
            }}
          >
            <option value="">All</option>
            <option value="return">Return Order</option>
            <option value="exchange">Exchange Order</option>
          </Select>
        </SimpleGrid>
        
        <HStack mt={4} spacing={2}>
          <Button
            leftIcon={<FiSearch />}
            colorScheme="blue"
            onClick={() => {
              fetchTransactions();
              fetchSummary();
            }}
            isLoading={loading}
          >
            Search
          </Button>
          <Button
            leftIcon={<FiRefreshCw />}
            onClick={handleResetFilters}
          >
            Reset
          </Button>
        </HStack>
      </Box>

      {/* Summary Cards - Moved after filters */}
      {summary && (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 6 }} spacing={4}>
          <Stat 
            bg="white" 
            p={4} 
            borderRadius="md" 
            boxShadow="sm"
            borderLeft="4px"
            borderColor="blue.500"
          >
            <HStack spacing={3} mb={2}>
              <Box
              p={2}
              borderRadius="md"
              bg="blue.50"
              color="blue.600"
            >
              <FiShoppingBag size={20} />
            </Box>
              <StatLabel fontSize="sm" color="gray.600">Total Orders</StatLabel>
            </HStack>
            <StatNumber fontSize="2xl" color="blue.600">{summary.total_orders || 0}</StatNumber>
            <StatHelpText fontSize="xs">
              {summary.first_order_date && summary.last_order_date && (
                `${formatDate(summary.first_order_date)} - ${formatDate(summary.last_order_date)}`
              )}
            </StatHelpText>
          </Stat>
          
          <Stat 
            bg="white" 
            p={4} 
            borderRadius="md" 
            boxShadow="sm"
            borderLeft="4px"
            borderColor="green.500"
          >
            <HStack spacing={3} mb={2}>
              <Box
              p={2}
              borderRadius="md"
              bg="green.50"
              color="green.600"
            >
              <FiUsers size={20} />
            </Box>
              <StatLabel fontSize="sm" color="gray.600">Total Customers</StatLabel>
            </HStack>
            <StatNumber fontSize="2xl" color="green.600">{summary.total_customers || 0}</StatNumber>
            <StatHelpText fontSize="xs">Unique customers</StatHelpText>
          </Stat>
          
          <Stat 
            bg="white" 
            p={4} 
            borderRadius="md" 
            boxShadow="sm"
            borderLeft="4px"
            borderColor="purple.500"
          >
            <HStack spacing={3} mb={2}>
              <Box
              p={2}
              borderRadius="md"
              bg="purple.50"
              color="purple.600"
            >
              <FiPackage size={20} />
            </Box>
              <StatLabel fontSize="sm" color="gray.600">Total Item</StatLabel>
            </HStack>
            <StatNumber fontSize="2xl" color="purple.600">{summary.total_items || 0}</StatNumber>
            <StatHelpText fontSize="xs">Items sold</StatHelpText>
          </Stat>
          
          <Stat 
            bg="white" 
            p={4} 
            borderRadius="md" 
            boxShadow="sm"
            borderLeft="4px"
            borderColor="orange.500"
          >
            <HStack spacing={3} mb={2}>
              <Box
              p={2}
              borderRadius="md"
              bg="orange.50"
              color="orange.600"
            >
              <FiDollarSign size={20} />
            </Box>
              <StatLabel fontSize="sm" color="gray.600">Total Net Amount</StatLabel>
            </HStack>
            <StatNumber fontSize="xl" color="orange.600">{formatCurrency(summary.total_net_amount || summary.total_revenue || 0)}</StatNumber>
            <StatHelpText fontSize="xs">Net sales</StatHelpText>
          </Stat>
          
          <Stat 
            bg="white" 
            p={4} 
            borderRadius="md" 
            boxShadow="sm"
            borderLeft="4px"
            borderColor="green.500"
          >
            <HStack spacing={3} mb={2}>
              <Box
              p={2}
              borderRadius="md"
              bg="green.50"
              color="green.600"
            >
              <FiDollarSign size={20} />
            </Box>
              <StatLabel fontSize="sm" color="gray.600">Total Gross Amount</StatLabel>
            </HStack>
            <StatNumber fontSize="xl" color="green.600">{formatCurrency(summary.total_gross_amount || summary.total_net_amount || summary.total_revenue || 0)}</StatNumber>
            <StatHelpText fontSize="xs">Gross sales</StatHelpText>
          </Stat>
          
          <Stat 
            bg="white" 
            p={4} 
            borderRadius="md" 
            boxShadow="sm"
            borderLeft="4px"
            borderColor="teal.500"
          >
            <HStack spacing={3} mb={2}>
              <Box
              p={2}
              borderRadius="md"
              bg="teal.50"
              color="teal.600"
            >
              <FiMapPin size={20} />
            </Box>
              <StatLabel fontSize="sm" color="gray.600">Total Per Location</StatLabel>
            </HStack>
            <StatNumber fontSize="xl" color="teal.600">{summary.total_per_location || 0}</StatNumber>
            <StatHelpText fontSize="xs">Locations</StatHelpText>
          </Stat>
        </SimpleGrid>
      )}

      {/* Exception Drill-down */}
      <Box bg="white" p={4} borderRadius="md" boxShadow="sm">
        <VStack align="stretch" spacing={3}>
          <HStack justify="space-between">
            <HStack spacing={2}>
              <FiAlertTriangle color="#D97706" />
              <Text fontWeight="700" color="gray.700">Exception Drill-down</Text>
            </HStack>
            <Text fontSize="xs" color="gray.500">Klik kartu untuk apply filter otomatis</Text>
          </HStack>
          <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={3}>
            <Card cursor="pointer" onClick={() => applyExceptionDrilldown('pending-return')} _hover={{ boxShadow: 'md' }}>
              <CardBody>
                <Text fontSize="xs" color="gray.500">Pending Return</Text>
                <Text fontSize="xl" fontWeight="700" color="orange.600">{exceptionInsights.pendingReturn}</Text>
              </CardBody>
            </Card>
            <Card cursor="pointer" onClick={() => applyExceptionDrilldown('return-order')} _hover={{ boxShadow: 'md' }}>
              <CardBody>
                <Text fontSize="xs" color="gray.500">Return Orders</Text>
                <Text fontSize="xl" fontWeight="700" color="red.600">{exceptionInsights.returnOrders}</Text>
              </CardBody>
            </Card>
            <Card cursor="pointer" onClick={() => applyExceptionDrilldown('exchange-order')} _hover={{ boxShadow: 'md' }}>
              <CardBody>
                <Text fontSize="xs" color="gray.500">Exchange Orders</Text>
                <Text fontSize="xl" fontWeight="700" color="purple.600">{exceptionInsights.exchangeOrders}</Text>
              </CardBody>
            </Card>
            <Card cursor="pointer" onClick={() => applyExceptionDrilldown('missing-customer')} _hover={{ boxShadow: 'md' }}>
              <CardBody>
                <Text fontSize="xs" color="gray.500">Missing Customer Name</Text>
                <Text fontSize="xl" fontWeight="700" color="gray.700">{exceptionInsights.missingCustomer}</Text>
              </CardBody>
            </Card>
          </SimpleGrid>
        </VStack>
      </Box>

      {/* Transactions Table - Moved to bottom */}
      <Box bg="white" p={4} borderRadius="md" boxShadow="sm">
        <style jsx global>{`
          .rdt_Table {
            width: 100%;
            overflow-x: auto;
          }
          .rdt_TableHeadRow {
            background-color: #f7fafc;
            font-weight: 600;
            font-size: 12px;
          }
          .rdt_TableRow {
            font-size: 13px;
          }
          .rdt_TableCell {
            padding: 12px 8px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .rdt_TableCol {
            padding: 12px 8px;
            white-space: nowrap;
          }
          .rdt_TableCol_Sortable {
            cursor: pointer;
          }
          .rdt_TableBody {
            overflow-x: auto;
          }
          @media (max-width: 768px) {
            .rdt_Table {
              font-size: 11px;
            }
            .rdt_TableCell {
              padding: 8px 4px;
            }
            .rdt_TableCol {
              padding: 8px 4px;
            }
          }
        `}</style>
        {loading ? (
          <Box textAlign="center" py={8}>
            <Spinner size="xl" />
          </Box>
        ) : (
          <Box width="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={searchText ? filteredTransactions : transactions}
              pagination
              paginationServer={!searchText}
              paginationTotalRows={searchText ? filteredTransactions.length : totalRows}
              paginationPerPage={filters.limit}
              paginationDefaultPage={searchText ? 1 : Math.floor(filters.offset / filters.limit) + 1}
              paginationRowsPerPageOptions={[10, 25, 50, 100, 200]}
              onChangePage={(page) => {
                if (!searchText) {
                  const newOffset = (page - 1) * filters.limit;
                  handleFilterChange('offset', newOffset);
                }
              }}
              onChangeRowsPerPage={(newLimit) => {
                handleFilterChange('limit', newLimit);
                handleFilterChange('offset', 0);
              }}
              subHeader
              subHeaderComponent={
                <Box mb={4}>
                  <InputGroup maxW="400px">
                    <InputLeftElement pointerEvents="none">
                      <FiSearch color="gray.300" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search by Order Ref, Customer, Receipt..."
                      value={searchText}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSearchText(value);
                        // Use client-side filtering for instant search
                      }}
                    />
                  </InputGroup>
                </Box>
              }
              onRowClicked={(row) => fetchTransactionDetail(row.id)}
              highlightOnHover
              pointerOnHover
              noDataComponent="No transactions found"
              responsive
              dense
              fixedHeader
              fixedHeaderScrollHeight="600px"
            />
          </Box>
        )}
      </Box>

      {/* Transaction Detail Modal */}
      <Modal isOpen={isDetailOpen} onClose={onDetailClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Transaction Detail</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedTransaction && (
              <VStack align="stretch" spacing={4}>
                {/* Order Info */}
                <Box>
                  <Text fontWeight="bold" mb={2}>Order Information</Text>
                  <TableContainer>
                    <Table size="sm" variant="simple">
                      <Tbody>
                        <Tr>
                          <Th>Order Ref</Th>
                          <Td>{selectedTransaction.order_ref || '-'}</Td>
                        </Tr>
                        <Tr>
                          <Th>Order Date</Th>
                          <Td>{formatDate(selectedTransaction.date_order)}</Td>
                        </Tr>
                        <Tr>
                          <Th>Customer Name</Th>
                          <Td>
                            {(() => {
                              const customerName = selectedTransaction.customer_name;
                              // If customer_name is null, empty, or '-', use referral_code2
                              if (!customerName || customerName.trim() === '' || customerName === '-') {
                                return selectedTransaction.referral_code2 || '-';
                              }
                              return customerName;
                            })()}
                          </Td>
                        </Tr>
                        <Tr>
                          <Th>Receipt Number</Th>
                          <Td>{selectedTransaction.receipt_number || '-'}</Td>
                        </Tr>
                        <Tr>
                          <Th>Location</Th>
                          <Td>{selectedTransaction.location || '-'}</Td>
                        </Tr>
                        <Tr>
                          <Th>Total</Th>
                          <Td fontWeight="bold">{formatCurrency(selectedTransaction.total)}</Td>
                        </Tr>
                        <Tr>
                          <Th>Total Paid</Th>
                          <Td>{formatCurrency(selectedTransaction.total_paid)}</Td>
                        </Tr>
                        <Tr>
                          <Th>Taxes</Th>
                          <Td>{formatCurrency(selectedTransaction.taxes)}</Td>
                        </Tr>
                        <Tr>
                          <Th>Loyalty Points</Th>
                          <Td>{selectedTransaction.loyalty_points || 0}</Td>
                        </Tr>
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>

                <Divider />

                {/* Items */}
                {selectedTransaction.items && selectedTransaction.items.length > 0 && (
                  <Box>
                    <Text fontWeight="bold" mb={2}>Order Items</Text>
                    <TableContainer>
                      <Table size="sm" variant="simple">
                        <Thead>
                          <Tr>
                            <Th>Product</Th>
                            <Th>Qty</Th>
                            <Th>Price</Th>
                            <Th>Subtotal</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {selectedTransaction.items.map((item, idx) => (
                            <Tr key={idx}>
                              <Td>
                                <VStack align="start" spacing={0}>
                                  <Text fontWeight="semibold">{item.product_name || item.product_code}</Text>
                                  {item.salesperson && (
                                    <Text fontSize="xs" color="gray.500">Sales: {item.salesperson}</Text>
                                  )}
                                  {item.taxes && item.taxes.length > 0 && (
                                    <Text fontSize="xs" color="gray.500">
                                      Tax: {item.taxes.map(t => `${t.tax_name} (${t.tax_amount}%)`).join(', ')}
                                    </Text>
                                  )}
                                </VStack>
                              </Td>
                              <Td>{item.qty} {item.uom || ''}</Td>
                              <Td>{formatCurrency(item.price_unit)}</Td>
                              <Td>{formatCurrency(item.subtotal)}</Td>
                            </Tr>
                          ))}
                          {/* Summary Row */}
                          <Tr bg="gray.50" fontWeight="bold">
                            <Td colSpan={2}>
                              <Text>Total</Text>
                            </Td>
                            <Td>
                              <Text>
                                {selectedTransaction.items.reduce((sum, item) => sum + (parseFloat(item.qty) || 0), 0)} {selectedTransaction.items[0]?.uom || ''}
                              </Text>
                            </Td>
                            <Td>
                              <Text>{formatCurrency(selectedTransaction.items.reduce((sum, item) => sum + (parseFloat(item.subtotal) || 0), 0))}</Text>
                            </Td>
                          </Tr>
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                <Divider />

                {/* Payments */}
                {selectedTransaction.payments && selectedTransaction.payments.length > 0 && (
                  <Box>
                    <Text fontWeight="bold" mb={2}>Payments</Text>
                    <TableContainer>
                      <Table size="sm" variant="simple">
                        <Thead>
                          <Tr>
                            <Th>Method</Th>
                            <Th>Amount</Th>
                            <Th>Date</Th>
                            <Th>Bank</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {selectedTransaction.payments.map((payment, idx) => (
                            <Tr key={idx}>
                              <Td>{payment.payment_method || '-'}</Td>
                              <Td>{formatCurrency(payment.amount)}</Td>
                              <Td>{formatDate(payment.payment_date)}</Td>
                              <Td>{payment.bank_name || '-'}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onDetailClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default ReportingTab;
